import { TextField, TextFieldProps } from '@material-ui/core'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

export interface HTMLNumericElement
  extends Omit<HTMLInputElement, 'value' | 'name'> {
  value: number | null | ''
  name?: string
}

export type CurrencyFieldProps = Omit<TextFieldProps, 'onChange'> & {
  value?: number | string
  onChange?(e: React.ChangeEvent<HTMLNumericElement>): void

  precision?: number
  thousandChar?: string
  decimalChar?: string
  maxValue?: number
  /** Digits are typed left to right and the decimal char is typed by the user */
  leftToRight?: boolean
}

function verifyNumber(string: string) {
  const numericRepresentation = string.replace(/[,.]/g, '')

  return {
    isNumber: !isNaN(Number(numericRepresentation)),
    numberFormat: !isNaN(Number(numericRepresentation))
      ? Number(numericRepresentation)
      : null
  }
}

function formatFixed(
  value: number | string | undefined,
  precision: number,
  thousandChar: string,
  decimalChar: string
) {
  if (value === undefined || value === null || value === '') return ''
  const number = Number(value)
  if (isNaN(number)) return ''

  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })
    .format(number)
    .replace(',', decimalChar)
    .replaceAll('.', thousandChar)
}

interface Draft {
  int: string
  dec: string
  decimal: boolean
}

function groupThousands(digits: string, thousandChar: string) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, thousandChar)
}

function LeftToRightCurrencyField(props: CurrencyFieldProps) {
  const {
    value,
    precision = 2,
    thousandChar = '.',
    decimalChar = ',',
    maxValue,
    leftToRight: _leftToRight,
    onChange,
    onBlur,
    onFocus,
    inputRef,
    ...inputProps
  } = props
  const [draft, setDraft] = useState<Draft | null>(null)
  const innerRef = useRef<HTMLInputElement | null>(null)

  // Fixed decimals are always shown; typed digits fill the integer part first
  // and, after the decimal char is typed, the decimal part
  function render(int: string, dec: string) {
    const integer = groupThousands(int || '0', thousandChar)
    return precision > 0
      ? `${integer}${decimalChar}${dec.padEnd(precision, '0')}`
      : integer
  }

  const display =
    draft !== null
      ? render(draft.int, draft.dec)
      : formatFixed(value, precision, thousandChar, decimalChar)

  useLayoutEffect(() => {
    if (draft === null || !innerRef.current) return
    const integerLength = groupThousands(draft.int || '0', thousandChar).length
    const caret = draft.decimal
      ? integerLength + 1 + draft.dec.length
      : integerLength
    innerRef.current.setSelectionRange(caret, caret)
  }, [draft, thousandChar])

  function setRefs(el: HTMLInputElement | null) {
    innerRef.current = el
    if (typeof inputRef === 'function') inputRef(el)
    else if (inputRef) {
      ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        el
    }
  }

  function emit(e: React.SyntheticEvent, next: number | '') {
    const target = { name: props.name, value: next }
    onChange?.({
      ...e,
      currentTarget: target,
      target
    } as unknown as React.ChangeEvent<HTMLNumericElement>)
  }

  function commit(e: React.SyntheticEvent, next: Draft) {
    if (next.int === '' && next.dec === '') {
      setDraft(next)
      return emit(e, '')
    }

    const numeric = Number(`${next.int || '0'}.${next.dec || '0'}`)
    if (maxValue && numeric > maxValue) return

    setDraft(next)
    emit(e, numeric)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (draft === null || e.ctrlKey || e.metaKey || e.altKey) return
    const { key } = e

    if (/^\d$/.test(key)) {
      e.preventDefault()
      if (draft.decimal) {
        if (draft.dec.length >= precision) return
        return commit(e, { ...draft, dec: draft.dec + key })
      }
      const nextInt = (draft.int + key).replace(/^0+(?=\d)/, '')
      // With maxValue, a digit that no longer fits the integer part goes to the decimals
      if (
        precision > 0 &&
        maxValue &&
        Number(nextInt) > maxValue &&
        draft.int !== ''
      ) {
        return commit(e, { ...draft, dec: key, decimal: true })
      }
      return commit(e, { ...draft, int: nextInt })
    }

    // ArrowRight also jumps to the decimals
    if (key === ',' || key === '.' || key === 'ArrowRight') {
      e.preventDefault()
      if (precision > 0 && !draft.decimal) {
        setDraft({ ...draft, decimal: true })
      }
      return
    }

    if (key === 'Backspace') {
      e.preventDefault()
      if (draft.decimal) {
        if (draft.dec.length > 0) {
          return commit(e, { ...draft, dec: draft.dec.slice(0, -1) })
        }
        return commit(e, { ...draft, decimal: false })
      }
      return commit(e, { ...draft, int: draft.int.slice(0, -1) })
    }

    if (key === 'Delete') {
      e.preventDefault()
      return commit(e, { int: '', dec: '', decimal: false })
    }
  }

  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    const number = value === undefined || value === '' ? NaN : Number(value)
    if (isNaN(number) || number === 0) {
      setDraft({ int: '', dec: '', decimal: false })
    } else {
      const [int, dec = ''] = number.toFixed(precision).split('.')
      setDraft({
        int: int === '0' ? '' : int,
        dec: dec.replace(/0+$/, ''),
        decimal: false
      })
    }
    onFocus?.(e)
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    setDraft(null)
    onBlur?.(e)
  }

  return (
    <TextField
      {...inputProps}
      inputRef={setRefs}
      onKeyDown={handleKeyDown}
      // Typing is handled in onKeyDown; this only keeps the input controlled
      onChange={() => undefined}
      onBlur={handleBlur}
      onFocus={handleFocus}
      value={display}
    />
  )
}

function CurrencyField(props: CurrencyFieldProps) {
  if (props.leftToRight) return <LeftToRightCurrencyField {...props} />

  return <RightToLeftCurrencyField {...props} />
}

function RightToLeftCurrencyField(props: CurrencyFieldProps) {
  const {
    value,
    precision = 2,
    thousandChar = '.',
    decimalChar = ',',
    leftToRight: _leftToRight,
    ...inputProps
  } = props
  const defaultValue = value === '' ? NaN : Number(value)

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision
      }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thousandChar, decimalChar, precision]
  )

  if (!decimalChar) {
    throw new Error('Decimal char should not be an empty string!')
  }
  if (!thousandChar) {
    throw new Error('Thousand char should not be an empty string!')
  }

  function format(number: number) {
    const result = formatter
      .format(number)
      .replace(',', decimalChar)
      .replaceAll('.', thousandChar)

    return result
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === ' ') e.preventDefault()

    if (
      e.ctrlKey ||
      e.shiftKey ||
      e.key === 'Backspace' ||
      e.key === 'Enter' ||
      e.key === 'Tab' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowLeft' ||
      e.key === 'Delete'
    )
      return
    if (!verifyNumber(e.key).isNumber) e.preventDefault()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const newEvent: React.ChangeEvent<HTMLNumericElement> = {
      ...e,
      currentTarget: {
        ...e.currentTarget,
        name: props.name,
        value: 0
      },
      target: {
        ...e.target,
        name: props.name,
        value: 0
      }
    }
    let numericRepresentation = e.target.value

    numericRepresentation = numericRepresentation.replaceAll(thousandChar, '')
    numericRepresentation = numericRepresentation.replace(decimalChar, '')

    if (numericRepresentation === '') {
      e.target.value = ''
      newEvent.target.value = ''
      newEvent.currentTarget.value = ''
      return props.onChange && props.onChange(newEvent)
    }

    const { isNumber, numberFormat } = verifyNumber(numericRepresentation)

    if (isNumber && numberFormat !== null && numberFormat >= 0) {
      const withPrecision = numberFormat / 10 ** precision

      if (props.maxValue && withPrecision > props.maxValue) {
        return
      }

      const formattedNumber = format(withPrecision)

      newEvent.target.value = withPrecision
      newEvent.currentTarget.value = withPrecision

      e.target.value = formattedNumber

      props.onChange && props.onChange(newEvent)
    }
  }

  const hasValue = value !== undefined
  let inputDefaultValue
  let inputValue

  if (hasValue) {
    if (isNaN(defaultValue) || value === '') {
      inputValue = ''
    } else {
      inputValue = format(defaultValue)
    }
  }

  if (!hasValue && !isNaN(defaultValue)) {
    inputDefaultValue = format(defaultValue)
  }

  return (
    <TextField
      defaultValue={inputDefaultValue}
      {...inputProps}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      value={inputValue}
    />
  )
}

export default CurrencyField
